package no.vareflow.web

import no.vareflow.app.domain.*

// "Moderne visning" (test): progress rings, mini progress curves on category cards, icon badges
// and a gradient call-to-action button. Switched in Mer → Visning; can be removed again later.

private const val MODERN_KEY = "vareflow.ui_modern"

fun loadModernUi(): Boolean = runCatching { window.localStorage.getItem(MODERN_KEY) != "false" }.getOrDefault(true)

fun saveModernUi(value: Boolean) {
    runCatching { window.localStorage.setItem(MODERN_KEY, value.toString()) }
}

private val ICONS = mapOf(
    "hourglass" to """<path d="M7 3.5h10M7 20.5h10M8 3.5c0 4.5 8 4.5 8 8.5s-8 4-8 8.5M16 3.5c0 4.5-8 4.5-8 8.5s8 4 8 8.5"/>""",
    "check" to """<path d="m5 12.5 4.2 4.2L19 7"/>""",
    "progress" to """<circle cx="12" cy="12" r="7.5"/><path d="M12 4.5a7.5 7.5 0 0 1 0 15z" fill="currentColor" stroke="none"/>""",
    "pencil" to """<path d="M4.5 19.5h4l10-10-4-4-10 10z"/><path d="m13 7 4 4"/>""",
    "lock" to """<rect x="5.5" y="10.5" width="13" height="9.5" rx="2"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/>""",
    "archive" to """<rect x="4" y="4.5" width="16" height="4.5" rx="1"/><path d="M5.5 9v10h13V9M10 13h4"/>"""
)

private fun iconSvg(name: String) =
    """<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name]}</svg>"""

/** Soft badge with an icon; [led] shows a pulsing dot instead (used for Aktiv). */
fun El.microBadge(text: String, tone: String, icon: String? = null, led: Boolean = false) = span("mbadge tone-$tone") {
    if (led) span("led").attr("aria-hidden", "true")
    else if (icon != null) svg(iconSvg(icon), "mbadge-icon")
    span("", text)
}

fun El.inventoryStatusBadge(status: InventoryStatus, modern: Boolean) {
    if (!modern) { statusBadge(status.label, inventoryStatusTone(status)); return }
    when (status) {
        InventoryStatus.DRAFT -> microBadge(status.label, Tone.BROWN, "pencil")
        InventoryStatus.ACTIVE -> microBadge(status.label, Tone.GREEN, led = true)
        InventoryStatus.CLOSED -> microBadge(status.label, Tone.INDIGO, "lock")
        InventoryStatus.ARCHIVED -> microBadge(status.label, Tone.GREY, "archive")
    }
}

fun El.workStatusBadge(status: CategoryWorkStatus, modern: Boolean) {
    if (!modern) { statusBadge(status.label, workStatusTone(status)); return }
    when (status) {
        CategoryWorkStatus.NOT_STARTED -> microBadge(status.label, Tone.ORANGE, "hourglass")
        CategoryWorkStatus.IN_PROGRESS -> microBadge(status.label, Tone.AMBER, "progress")
        CategoryWorkStatus.DONE -> microBadge(status.label, Tone.GREEN, "check")
    }
}

/**
 * Two rings: outer = products counted (orange), inner = categories done (blue).
 * Animates from the previously shown values, so a redraw without changes does not replay it.
 */
fun El.progressRing(app: WebApp, key: String, percentCounted: Int, percentDone: Int) {
    val outerR = 52.0
    val innerR = 38.0
    val outerC = 2 * kotlin.math.PI * outerR
    val innerC = 2 * kotlin.math.PI * innerR
    val previous = app.lastRings[key]
    app.lastRings[key] = percentCounted to percentDone
    val fromOuter = previous?.first ?: 0
    val fromInner = previous?.second ?: 0
    fun offset(c: Double, p: Int) = c * (1 - p.coerceIn(0, 100) / 100.0)
    val animate = previous == null || previous != (percentCounted to percentDone)
    div("ring") {
        attr("role", "img")
        attr("aria-label", t("$percentCounted % telt, $percentDone % av kategoriene ferdig", "$percentCounted % counted, $percentDone % of the categories done"))
        svg(
            """<svg viewBox="0 0 128 128" width="128" height="128" aria-hidden="true">
              <defs><linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#FFB040"/><stop offset="1" stop-color="#FF5500"/></linearGradient></defs>
              <circle cx="64" cy="64" r="$outerR" class="ring-track" stroke-width="10"/>
              <circle cx="64" cy="64" r="$innerR" class="ring-track" stroke-width="7"/>
              <circle cx="64" cy="64" r="$outerR" class="ring-value ${if (animate) "ring-anim" else ""}" stroke="url(#ringGrad)" stroke-width="10"
                stroke-dasharray="$outerC" style="--from:${offset(outerC, fromOuter)};--to:${offset(outerC, percentCounted)};stroke-dashoffset:${offset(outerC, percentCounted)}"/>
              <circle cx="64" cy="64" r="$innerR" class="ring-value ring-inner ${if (animate) "ring-anim" else ""}" stroke-width="7"
                stroke-dasharray="$innerC" style="--from:${offset(innerC, fromInner)};--to:${offset(innerC, percentDone)};stroke-dashoffset:${offset(innerC, percentDone)}"/>
            </svg>""",
            "ring-svg"
        )
        div("ring-center") {
            div("ring-percent", "$percentCounted%")
            div("ring-caption", t("telt", "counted"))
        }
    }
}

/**
 * Small smooth area curve of the counting pace (changes per time slice, from the audit log).
 * A category with no counts gets a faint flat baseline.
 */
fun El.sparkline(pace: List<Int>, tone: String) {
    val w = 160.0
    val h = 36.0
    if (pace.isEmpty() || pace.all { it == 0 }) {
        svg("""<svg viewBox="0 0 $w $h" preserveAspectRatio="none" width="100%" height="36" aria-hidden="true">
            <path d="M0 ${h - 3} L$w ${h - 3}" class="spark-base" vector-effect="non-scaling-stroke"/></svg>""", "spark")
        return
    }
    // Light smoothing so single busy minutes do not look like noise.
    val smooth = pace.indices.map { i ->
        val window = listOfNotNull(pace.getOrNull(i - 1), pace[i], pace[i], pace.getOrNull(i + 1))
        window.sum().toDouble() / window.size
    }
    val max = smooth.max()
    val pts = smooth.mapIndexed { i, v ->
        val x = if (pace.size == 1) w else i * w / (pace.size - 1)
        x to (h - 3 - v / max * (h - 8))
    }
    // Smooth curve through the points (midpoint quadratic Béziers).
    val d = StringBuilder("M${round1(pts[0].first)} ${round1(pts[0].second)}")
    for (i in 1 until pts.size) {
        val (px, py) = pts[i - 1]
        val (x, y) = pts[i]
        val mx = (px + x) / 2
        val my = (py + y) / 2
        d.append(" Q${round1(px)} ${round1(py)} ${round1(mx)} ${round1(my)}")
        if (i == pts.lastIndex) d.append(" T${round1(x)} ${round1(y)}")
    }
    val line = d.toString()
    svg(
        """<svg viewBox="0 0 $w $h" preserveAspectRatio="none" width="100%" height="36" aria-hidden="true">
          <path d="$line L$w $h L0 $h Z" class="spark-area spark-$tone"/>
          <path d="$line" class="spark-line spark-$tone" vector-effect="non-scaling-stroke"/>
        </svg>""",
        "spark"
    )
}

private fun round1(v: Double): String = (kotlin.math.round(v * 10) / 10).toString()
